import { cn } from "@zero/utils";
import { ArrowDownAZ, ArrowUpAZ, CheckCircle2, Circle, Clock3, ListTodo, Search, Tags, Trash2 } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { useTodoStore } from "../store/todoStore";
import { CategoryManager } from "./CategoryManager";

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon: React.ReactNode;
}

function FilterButton({ active, onClick, children, icon }: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center gap-2 h-8 px-3 rounded-md text-sm font-medium transition-all",
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-secondary hover:text-foreground",
      )}
    >
      {icon}
      {children}
    </button>
  );
}

export function TodoFilters() {
  const { filter, setFilter, search, setSearch, activeCount, completedCount, clearCompleted, todos, categories } =
    useTodoStore();
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  const active = activeCount();
  const completed = completedCount();
  const total = todos.length;
  const categoryNames = categories();

  const hasCompleted = completed > 0;

  return (
    <>
      <div className="mb-6 space-y-3">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          {/* Status Filters */}
          <div className="flex flex-wrap gap-2">
            <FilterButton
              active={filter.status === "all"}
              onClick={() => setFilter({ ...filter, status: "all" })}
              icon={<ListTodo size={16} />}
            >
              全部 ({total})
            </FilterButton>
            <FilterButton
              active={filter.status === "active"}
              onClick={() => setFilter({ ...filter, status: "active" })}
              icon={<Circle size={16} />}
            >
              进行中 ({active})
            </FilterButton>
            <FilterButton
              active={filter.status === "pending"}
              onClick={() => setFilter({ ...filter, status: "pending" })}
              icon={<Circle size={16} />}
            >
              待处理
            </FilterButton>
            <FilterButton
              active={filter.status === "inProgress"}
              onClick={() => setFilter({ ...filter, status: "inProgress" })}
              icon={<Clock3 size={16} />}
            >
              处理中
            </FilterButton>
            <FilterButton
              active={filter.status === "completed"}
              onClick={() => setFilter({ ...filter, status: "completed" })}
              icon={<CheckCircle2 size={16} />}
            >
              已完成 ({completed})
            </FilterButton>
          </div>

          {/* Clear Completed */}
          {hasCompleted && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              type="button"
              onClick={clearCompleted}
              className="inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-sm text-destructive transition-colors hover:bg-destructive/10"
            >
              <Trash2 size={16} />
              清除已完成
            </motion.button>
          )}
        </div>

        <div className="grid gap-2 lg:grid-cols-[1fr_160px_180px_170px_120px_auto]">
          <label className="relative block">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="搜索标题、描述、分类或标签"
              className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
            />
          </label>

          <select
            value={filter.ownership ?? "allVisible"}
            onChange={(event) =>
              setFilter({
                ...filter,
                ownership: event.target.value as NonNullable<typeof filter.ownership>,
              })
            }
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
            aria-label="关系筛选"
          >
            <option value="allVisible">全部可见</option>
            <option value="ownedByMe">我创建的</option>
            <option value="assignedToMe">指派给我</option>
          </select>

          <select
            value={filter.category ?? ""}
            onChange={(event) => setFilter({ ...filter, category: event.target.value || undefined })}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
            aria-label="分类筛选"
          >
            <option value="">全部分类</option>
            {categoryNames.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <select
            value={filter.sortBy ?? "createdAt"}
            onChange={(event) =>
              setFilter({
                ...filter,
                sortBy: event.target.value as NonNullable<typeof filter.sortBy>,
              })
            }
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
            aria-label="排序字段"
          >
            <option value="createdAt">创建时间</option>
            <option value="updatedAt">更新时间</option>
            <option value="dueDate">截止日期</option>
            <option value="priority">优先级</option>
            <option value="status">状态</option>
          </select>

          <button
            type="button"
            onClick={() =>
              setFilter({
                ...filter,
                sortDirection: filter.sortDirection === "asc" ? "desc" : "asc",
              })
            }
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-input px-3 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {filter.sortDirection === "asc" ? <ArrowUpAZ size={16} /> : <ArrowDownAZ size={16} />}
            {filter.sortDirection === "asc" ? "升序" : "降序"}
          </button>

          <button
            type="button"
            onClick={() => setCategoryManagerOpen((open) => !open)}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-secondary px-3 text-sm font-medium text-secondary-foreground hover:bg-secondary/80"
          >
            <Tags size={16} />
            分类
          </button>
        </div>
      </div>

      <CategoryManager open={categoryManagerOpen} onClose={() => setCategoryManagerOpen(false)} />
    </>
  );
}
